'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { InterviewPhase } from '@/lib/interviewPhase';

export type TextConnectionStatus = 'idle' | 'connecting' | 'ready' | 'error';

export interface TextTranscriptEntry {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  timestamp: string;
  /** opening → interviewing → closing at the time this turn was recorded. */
  phase: InterviewPhase;
}

export type TextTestingMode = 'manual' | 'automatic';

export type UseTextRealtimeSessionOptions = {
  /** System instructions (study realtime prompt). */
  instructions: string;
  /** Closing message returned via end_interview tool output. */
  closingMessage: string;
  /** Registry study id — required to persist under automatic-testing/. */
  studyId: string;
  /** Current UI mode (stored in metadata on each persist). */
  mode: TextTestingMode;
  /** Simulated-user style when mode is automatic. */
  userStyle?: string;
};

export type UseTextRealtimeSessionResult = {
  status: TextConnectionStatus;
  sessionId: string | null;
  transcript: TextTranscriptEntry[];
  streamingText: string;
  ended: boolean;
  error: string | null;
  /** True when the assistant finished a turn and is waiting for user input. */
  awaitingUser: boolean;
  start: () => Promise<void>;
  stop: () => void;
  /** Returns true if the user message was accepted and sent on the socket. */
  sendText: (text: string) => boolean;
};

const END_INTERVIEW_TOOL = {
  type: 'function' as const,
  name: 'end_interview',
  description:
    'Signal that the interview is ready to conclude. Call this alone in its own ' +
    'turn — do NOT speak the closing message in the same response. The closing ' +
    'message will be delivered automatically after this tool executes.',
  parameters: {
    type: 'object',
    properties: {},
    additionalProperties: false,
  },
};

type ServerEvent = {
  type: string;
  delta?: string;
  text?: string;
  item_id?: string;
  call_id?: string;
  name?: string;
  error?: { message?: string } | string;
  response?: {
    output?: Array<{ type?: string; name?: string; call_id?: string }>;
  };
};

/**
 * Independent Realtime WebSocket path for text-only interview testing.
 * Does not share state or transport with useRealtimeSession (audio/WebRTC).
 */
export function useTextRealtimeSession(
  options: UseTextRealtimeSessionOptions,
): UseTextRealtimeSessionResult {
  const instructionsRef = useRef(options.instructions);
  instructionsRef.current = options.instructions;
  const closingMessageRef = useRef(options.closingMessage);
  closingMessageRef.current = options.closingMessage;
  const studyIdRef = useRef(options.studyId);
  studyIdRef.current = options.studyId;
  const modeRef = useRef(options.mode);
  modeRef.current = options.mode;
  const userStyleRef = useRef(options.userStyle);
  userStyleRef.current = options.userStyle;

  const [status, setStatus] = useState<TextConnectionStatus>('idle');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<TextTranscriptEntry[]>([]);
  const [streamingText, setStreamingText] = useState('');
  const [ended, setEnded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [awaitingUser, setAwaitingUser] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const transcriptRef = useRef<TextTranscriptEntry[]>([]);
  const streamBufRef = useRef('');
  const sessionConfiguredRef = useRef(false);
  const openingRequestedRef = useRef(false);
  const awaitingClosingRef = useRef(false);
  const endedRef = useRef(false);
  const statusRef = useRef<TextConnectionStatus>('idle');
  const handledCallIdsRef = useRef<Set<string>>(new Set());
  /** True if the in-flight response produced at least one assistant text item. */
  const responseHadTextRef = useRef(false);
  const phaseRef = useRef<InterviewPhase>('opening');
  /** Captured at response.created — opening AI text may flush after phase flips. */
  const responseIsOpeningRef = useRef(false);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const sendEvent = useCallback((event: Record<string, unknown>) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify(event));
  }, []);

  const persistTranscript = useCallback((opts?: { ended?: boolean }) => {
    const sid = sessionIdRef.current;
    const studyId = studyIdRef.current;
    if (!sid || !studyId || transcriptRef.current.length === 0) return;
    fetch('/api/save-text-testing', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: sid,
        studyId,
        mode: modeRef.current,
        userStyle: modeRef.current === 'automatic' ? userStyleRef.current : undefined,
        ended: opts?.ended ?? endedRef.current,
        transcript: transcriptRef.current,
      }),
    }).catch(console.error);
  }, []);

  const appendTranscript = useCallback((entry: TextTranscriptEntry) => {
    const next = [...transcriptRef.current, entry];
    transcriptRef.current = next;
    setTranscript(next);
    persistTranscript();
  }, [persistTranscript]);

  const handleEndInterview = useCallback(
    (callId: string) => {
      if (handledCallIdsRef.current.has(callId)) return;
      handledCallIdsRef.current.add(callId);
      awaitingClosingRef.current = true;
      phaseRef.current = 'closing';
      sendEvent({
        type: 'conversation.item.create',
        item: {
          type: 'function_call_output',
          call_id: callId,
          output: JSON.stringify({
            status: 'end_acknowledged',
            instruction:
              'Deliver the following closing message verbatim as text. Do not call end_interview again.',
            closing_message: closingMessageRef.current,
          }),
        },
      });
      sendEvent({
        type: 'response.create',
        response: { output_modalities: ['text'] },
      });
    },
    [sendEvent],
  );

  const handleServerEventRef = useRef<(raw: ServerEvent) => void>(() => {});

  handleServerEventRef.current = (raw: ServerEvent) => {
    const type = raw.type;

    if (type === 'error') {
      const msg =
        typeof raw.error === 'string'
          ? raw.error
          : raw.error?.message ?? 'Realtime error';
      setError(msg);
      setStatus('error');
      return;
    }

    if (type === 'session.created' && !sessionConfiguredRef.current) {
      sessionConfiguredRef.current = true;
      sendEvent({
        type: 'session.update',
        session: {
          type: 'realtime',
          output_modalities: ['text'],
          instructions: instructionsRef.current,
          tools: [END_INTERVIEW_TOOL],
          tool_choice: 'auto',
        },
      });
      return;
    }

    if (type === 'session.updated' && !openingRequestedRef.current) {
      openingRequestedRef.current = true;
      setStatus('ready');
      setAwaitingUser(false);
      sendEvent({
        type: 'response.create',
        response: { output_modalities: ['text'] },
      });
      return;
    }

    if (type === 'response.output_text.delta' || type === 'response.text.delta') {
      const delta = raw.delta ?? '';
      if (!delta) return;
      streamBufRef.current += delta;
      setStreamingText(streamBufRef.current);
      setAwaitingUser(false);
      return;
    }

    if (type === 'response.created') {
      responseHadTextRef.current = false;
      responseIsOpeningRef.current = phaseRef.current === 'opening';
      return;
    }

    if (type === 'response.output_text.done' || type === 'response.text.done') {
      const text = (raw.text ?? streamBufRef.current).trim();
      streamBufRef.current = '';
      setStreamingText('');
      if (text) {
        responseHadTextRef.current = true;
        const turnPhase: InterviewPhase = awaitingClosingRef.current
          ? 'closing'
          : responseIsOpeningRef.current
            ? 'opening'
            : phaseRef.current;
        appendTranscript({
          id: raw.item_id ?? `asst-${Date.now()}`,
          role: 'assistant',
          text,
          timestamp: new Date().toISOString(),
          phase: turnPhase,
        });
      }
      // Closing message may be the only text in a follow-up response after end_interview.
      if (awaitingClosingRef.current && text) {
        awaitingClosingRef.current = false;
        endedRef.current = true;
        setEnded(true);
        setAwaitingUser(false);
        persistTranscript({ ended: true });
      }
      // Do not set awaitingUser here — wait for response.done so multi-item
      // assistant responses (e.g. transition + main question) finish first.
      return;
    }

    if (type === 'response.function_call_arguments.done') {
      if (raw.name === 'end_interview' && raw.call_id) {
        setAwaitingUser(false);
        phaseRef.current = 'closing';
        handleEndInterview(raw.call_id);
      }
      return;
    }

    if (type === 'response.done') {
      const call = raw.response?.output?.find(
        (item) => item.type === 'function_call' && item.name === 'end_interview',
      );
      if (call?.call_id && !endedRef.current) {
        setAwaitingUser(false);
        phaseRef.current = 'closing';
        handleEndInterview(call.call_id);
        return;
      }
      if (
        !endedRef.current
        && !awaitingClosingRef.current
        && responseHadTextRef.current
      ) {
        setAwaitingUser(true);
      }
    }
  };

  const stop = useCallback(() => {
    if (sessionIdRef.current && transcriptRef.current.length > 0) {
      persistTranscript({ ended: endedRef.current });
    }
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      try {
        ws.close();
      } catch {
        // ignore
      }
    }
    sessionConfiguredRef.current = false;
    openingRequestedRef.current = false;
    awaitingClosingRef.current = false;
    handledCallIdsRef.current = new Set();
    responseHadTextRef.current = false;
    responseIsOpeningRef.current = false;
    phaseRef.current = 'opening';
    streamBufRef.current = '';
    setStreamingText('');
    setAwaitingUser(false);
    setStatus('idle');
  }, [persistTranscript]);

  const start = useCallback(async () => {
    stop();
    setStatus('connecting');
    setError(null);
    setEnded(false);
    endedRef.current = false;
    setTranscript([]);
    transcriptRef.current = [];
    setStreamingText('');
    streamBufRef.current = '';
    setAwaitingUser(false);
    sessionConfiguredRef.current = false;
    openingRequestedRef.current = false;
    awaitingClosingRef.current = false;
    handledCallIdsRef.current = new Set();
    responseHadTextRef.current = false;
    responseIsOpeningRef.current = false;
    phaseRef.current = 'opening';

    {
      const now = new Date();
      const p = (n: number) => String(n).padStart(2, '0');
      const datePart = `${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}`;
      const timePart = `${p(now.getHours())}${p(now.getMinutes())}${p(now.getSeconds())}`;
      const suffix = Math.random().toString(36).slice(2, 7);
      const sid = `${studyIdRef.current}-text-${datePart}-${timePart}-${suffix}`;
      sessionIdRef.current = sid;
      setSessionId(sid);
    }

    try {
      const sessResp = await fetch('/api/realtime/text-session', { method: 'POST' });
      if (!sessResp.ok) {
        const e = await sessResp.json().catch(() => ({ error: sessResp.statusText }));
        throw new Error((e as { error?: string }).error ?? 'Failed to create text session');
      }
      const sessData = (await sessResp.json()) as { value?: string; model?: string };
      const ephemeralKey = sessData.value;
      const model = sessData.model ?? 'gpt-realtime-2';
      if (!ephemeralKey) throw new Error('No ephemeral key in text session response');

      const url = `wss://api.openai.com/v1/realtime?model=${encodeURIComponent(model)}`;
      const ws = new WebSocket(url, [
        'realtime',
        `openai-insecure-api-key.${ephemeralKey}`,
      ]);
      wsRef.current = ws;

      await new Promise<void>((resolve, reject) => {
        const onOpen = () => {
          cleanup();
          resolve();
        };
        const onError = () => {
          cleanup();
          reject(new Error('WebSocket connection failed'));
        };
        const cleanup = () => {
          ws.removeEventListener('open', onOpen);
          ws.removeEventListener('error', onError);
        };
        ws.addEventListener('open', onOpen);
        ws.addEventListener('error', onError);
      });

      ws.addEventListener('message', (ev) => {
        try {
          const data = JSON.parse(String(ev.data)) as ServerEvent;
          handleServerEventRef.current(data);
        } catch (err) {
          console.error('[text-realtime] failed to parse event', err);
        }
      });

      ws.addEventListener('close', () => {
        if (wsRef.current === ws) {
          wsRef.current = null;
          if (!endedRef.current) {
            setStatus((prev) => (prev === 'error' ? prev : 'idle'));
          }
          setAwaitingUser(false);
        }
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      setStatus('error');
      stop();
    }
  }, [stop]);

  const sendText = useCallback(
    (text: string): boolean => {
      const trimmed = text.trim();
      if (!trimmed || endedRef.current) return false;
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN || statusRef.current !== 'ready') {
        return false;
      }

      if (phaseRef.current === 'opening') {
        // Stamp ready-reply as opening, then advance session phase.
        appendTranscript({
          id: `user-${Date.now()}`,
          role: 'user',
          text: trimmed,
          timestamp: new Date().toISOString(),
          phase: 'opening',
        });
        phaseRef.current = 'interviewing';
      } else {
        appendTranscript({
          id: `user-${Date.now()}`,
          role: 'user',
          text: trimmed,
          timestamp: new Date().toISOString(),
          phase: phaseRef.current,
        });
      }
      setAwaitingUser(false);
      sendEvent({
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'user',
          content: [{ type: 'input_text', text: trimmed }],
        },
      });
      sendEvent({
        type: 'response.create',
        response: { output_modalities: ['text'] },
      });
      return true;
    },
    [appendTranscript, sendEvent],
  );

  return {
    status,
    sessionId,
    transcript,
    streamingText,
    ended,
    error,
    awaitingUser,
    start,
    stop,
    sendText,
  };
}
